import { useMemo } from 'react'
import { useAuth } from './useAuth'
import { useUserEventInterests } from './useUserEventInterests'
import { useSocialEventData } from './useSocialEventData'
import { useRegionalEvents } from './useRegionalEvents'
import { useVisibleEvents } from './useVisibleEvents'
import { useGuestGuard } from './useGuestGuard'
import { interestPrompt } from '../lib/guestPrompts'
import type { EventType } from '../types'

/**
 * The data chain behind the discovery pages (Home, Festivals, Parties): the
 * viewer's RSVPs, peer facepiles, region-scoped events with cancelled ones
 * dropped unless the viewer planned them, and the guest-guarded RSVP setter.
 * `scopedEvents` is what every section should read — a party back home
 * shouldn't follow you to another city.
 */
export function useDiscoveryFeed({ type }: { type?: EventType } = {}) {
  const { user, signedInUid, loading: authLoading } = useAuth()
  const { eventStatuses, loading: interestsLoading, setEventInterest } = useUserEventInterests(user?.uid ?? null)
  const social = useSocialEventData(eventStatuses)
  const regional = useRegionalEvents(eventStatuses, { type })
  const events = useVisibleEvents(regional.events, eventStatuses, social.allGroupEvents)
  const { guard } = useGuestGuard()
  const guardedSetEventInterest = guard(setEventInterest, interestPrompt('event_interest'))

  const { inScope } = regional
  const scopedEvents = useMemo(
    () => events.filter(e => inScope(e, eventStatuses.get(e.id))),
    [events, inScope, eventStatuses],
  )

  return {
    user,
    signedInUid,
    authLoading,
    eventStatuses,
    interestsLoading,
    guardedSetEventInterest,
    ...social,
    ...regional,
    /** Visible (cancelled-filtered) events in every region; prefer `scopedEvents`. */
    events,
    scopedEvents,
  }
}
